import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { callLLM } from './llmService';
import reportData from './report_data.json';

// Styled Components
const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #F8FAFF;
  font-family: 'Raleway', sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const ContainerMd = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const HeaderContent = styled.div``;

const Title = styled.h1`
  font-family: 'Raleway', sans-serif;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666D79;
  margin: 0;
`;

const PrimaryButton = styled.button`
  background-color: #2A3039;
  color: #FFFFFF;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #666D79;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: #666D79;
  padding: 8px 16px;
  border: 1px solid #DFDFDF;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #F8FAFF;
    color: #2A3039;
  }
`;

const Card = styled.div`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #F2F2F2;
`;

const CardTitle = styled.h2`
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 24px;
`;

const BackButton = styled.button`
  color: #666D79;
  background: none;
  border: none;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #2A3039;
  }

  svg {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background-color: #F2F2F2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 32px;
    height: 32px;
    color: #666D79;
  }
`;

const EmptyTitle = styled.h3`
  font-family: 'Raleway', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666D79;
  margin: 0 0 24px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F8FAFF;
`;

const TableHeaderCell = styled.th`
  padding: 12px 24px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #666D79;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #F2F2F2;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #F8FAFF;
  }
`;

const TableCell = styled.td`
  padding: 16px 24px;
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #2A3039;

  &.secondary {
    color: #666D79;
  }
`;

const Badge = styled.span`
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  background-color: #D4F5ED;
  color: #1D9A7D;
  border-radius: 20px;
`;

const Grid = styled.div`
  display: grid;
  gap: 24px;
`;

const ReportCard = styled.div`
  background-color: #FFFFFF;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid #F2F2F2;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #B3B3B3;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const ReportTitle = styled.h3`
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0;
`;

const ScoreDisplay = styled.div`
  text-align: right;
`;

const ScoreValue = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  color: #2A3039;
  margin: 0;
`;

const ScoreLabel = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #666D79;
  margin-top: 4px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const MetricItem = styled.div``;

const MetricLabel = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #666D79;
  margin-bottom: 4px;
`;

const MetricValue = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;

  &.positive {
    color: #27CDA7;
  }

  &.negative {
    color: #F15062;
  }
`;

const FactorTags = styled.div``;

const TagsLabel = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #666D79;
  margin-bottom: 8px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FactorTag = styled.span`
  padding: 6px 12px;
  background-color: #FCDCE0;
  color: #F15062;
  font-size: 12px;
  border-radius: 20px;
`;

const FactorCard = styled.div`
  background-color: #FFFFFF;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid #F2F2F2;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #B3B3B3;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const FactorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const FactorTitle = styled.h3`
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0;
`;

const FactorScore = styled.div`
  text-align: right;
`;

const FactorScoreValue = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  color: #F15062;
  margin: 0;
`;

const FactorSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin-bottom: 8px;
`;

const SectionContent = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  color: #666D79;
`;

const ChallengesList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style: none;
`;

const ChallengeItem = styled.li`
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666D79;
  margin-bottom: 4px;
  display: flex;
  align-items: flex-start;

  &:before {
    content: "•";
    color: #F15062;
    margin-right: 8px;
  }
`;

const ConversationContainer = styled.div`
  margin-bottom: 32px;
`;

const ConversationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ConversationInfo = styled.div``;

const ConversationTitle = styled.h1`
  font-family: 'Raleway', sans-serif;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0 0 8px 0;
`;

const ConversationSubtitle = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666D79;
  margin: 0;
`;

const TokenInfo = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #666D79;
`;

const ProgressBar = styled.div`
  width: 100%;
  background-color: #F2F2F2;
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #2A3039;
  transition: width 0.3s ease;
`;

const ChatContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 60vh;
  min-height: 500px;
  display: flex;
  flex-direction: column;
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessageContainer = styled.div`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const Message = styled.div`
  max-width: ${props => props.isUser ? '320px' : '600px'};
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${props => props.isUser ? '#2A3039' : '#F2F2F2'};
  color: ${props => props.isUser ? '#FFFFFF' : '#2A3039'};
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const LoadingMessage = styled.div`
  background-color: #F2F2F2;
  color: #2A3039;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  gap: 4px;
`;

const LoadingDot = styled.div`
  width: 8px;
  height: 8px;
  background-color: #666D79;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;

  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }

  @keyframes bounce {
    0%, 80%, 100% {
      transform: scale(0);
    } 40% {
      transform: scale(1);
    }
  }
`;

const ChatInput = styled.div`
  border-top: 1px solid #F2F2F2;
  padding: 16px;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const TextInput = styled.textarea`
  flex: 1;
  border: 1px solid #DFDFDF;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  outline: none;
  resize: none;
  min-height: 20px;
  max-height: 72px; /* Approximately 3 lines */
  line-height: 1.5;
  transition: border-color 0.2s ease;
  overflow-y: auto;

  &:focus {
    border-color: #666D79;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  background-color: #2A3039;
  color: #FFFFFF;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #666D79;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormulateContainer = styled.div`
  margin-bottom: 32px;
`;

const FormulateTitle = styled.h1`
  font-family: 'Raleway', sans-serif;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0 0 8px 0;
`;

const FormulateSubtitle = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #666D79;
  margin: 0;
`;

const FormulateCard = styled.div`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
`;

const ActionsHeader = styled.div`
  margin-bottom: 24px;
`;

const ActionsTitle = styled.h3`
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #2A3039;
  margin: 0 0 8px 0;
`;

const ActionsDescription = styled.p`
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #666D79;
  margin: 0;
`;

const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const ActionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const ActionNumber = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background-color: #F2F2F2;
  color: #2A3039;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
`;

const ActionTextarea = styled.textarea`
  flex: 1;
  border: 1px solid #DFDFDF;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  outline: none;
  resize: none;
  rows: 2;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #666D79;
  }
`;

const ActionDeleteButton = styled.button`
  flex-shrink: 0;
  color: #F15062;
  background: none;
  border: none;
  cursor: pointer;
  margin-top: 8px;
  transition: color 0.2s ease;

  &:hover {
    color: #2A3039;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FormulateFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AddActionButton = styled.button`
  color: #666D79;
  background: none;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;

  &:hover {
    color: #2A3039;
  }

  svg {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const CompleteButton = styled.button`
  background-color: #27CDA7;
  color: #FFFFFF;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #1D9A7D;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionPlanCenter = () => {
  // State management
  const [currentStep, setCurrentStep] = useState('overview'); // overview, selectReport, selectFactor, conversation, formulate
  const [activePlans, setActivePlans] = useState([]); // TODO: Load from storage/API
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [proposedActions, setProposedActions] = useState([]);
  const [conversationTurn, setConversationTurn] = useState(0);
  const [tokenUsage, setTokenUsage] = useState({ total_tokens: 0, total_cost: 0 });
  
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const maxConversationTurns = 5;

  // Auto-scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  // Auto-resize textarea
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 72) + 'px';
    }
  };

  // Save plans to localStorage (simple persistence)
  const savePlan = (plan) => {
    const savedPlans = JSON.parse(localStorage.getItem('actionPlans') || '[]');
    const newPlan = {
      id: Date.now().toString(),
      reportName: selectedReport.anchor_name,
      factorName: selectedFactor.factor_name,
      actions: plan,
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    savedPlans.push(newPlan);
    localStorage.setItem('actionPlans', JSON.stringify(savedPlans));
    setActivePlans(savedPlans);
  };

  // Load plans from localStorage
  useEffect(() => {
    const savedPlans = JSON.parse(localStorage.getItem('actionPlans') || '[]');
    setActivePlans(savedPlans);
  }, []);

  // Token update handler
  const handleTokenUpdate = (newTokens) => {
    setTokenUsage(prev => ({
      total_tokens: prev.total_tokens + newTokens.total_tokens,
      total_cost: prev.total_cost + (newTokens.total_tokens * 0.002 / 1000) // Rough estimate
    }));
  };

  // Start conversation with initial LLM question
  const startConversation = async (factor) => {
    setCurrentStep('conversation');
    setIsLoading(true);
    
    const contextPrompt = `
    You are facilitating an action planning session for a manager. They have selected the "${factor.factor_name}" factor which scored ${factor.latest_score.toFixed(2)} out of 10.
    
    Context about this factor:
    - Definition: ${factor.definition}
    - Summary: ${factor.summary}
    
    Analysis insights:
    - What this means for the organization: ${factor.analysis?.what_this_means_for_you?.join('; ') || 'No specific insights listed'}
    
    - Day-to-day impacts: ${factor.analysis?.day_to_day_impacts?.join('; ') || 'No specific impacts listed'}
    
    - Key takeaways: ${factor.analysis?.key_takeaways?.join('; ') || 'No specific takeaways listed'}
    
    - Representative employee quotes: ${factor.analysis?.impactful_quotes?.slice(0, 3).join('; ') || 'No quotes available'}
    
    Your goal is to help the manager build a concrete action plan through 5 thoughtful questions. This is question 1 of 5.
    
    Start by asking an opening question that helps the manager think about the root causes of the low score in this factor. Use the specific insights and quotes above to make your question more targeted and relevant to their actual situation.
    
    Return only the question, no additional formatting.
    `;

    try {
      const response = await callLLM(contextPrompt, handleTokenUpdate);
      setConversationHistory([{
        type: 'assistant',
        message: response.content,
        timestamp: new Date().toISOString()
      }]);
      setConversationTurn(1);
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
    
    setIsLoading(false);
  };

  // Handle user input in conversation
  const handleConversationInput = async () => {
    if (!currentInput.trim()) return;
    
    // Add user message to history
    const userMessage = {
      type: 'user',
      message: currentInput,
      timestamp: new Date().toISOString()
    };
    
    setConversationHistory(prev => [...prev, userMessage]);
    setCurrentInput('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    // Prepare conversation context for LLM
    const conversationContext = conversationHistory
      .concat([userMessage])
      .map(msg => `${msg.type}: ${msg.message}`)
      .join('\n');

    const nextTurn = conversationTurn + 1;
    
    if (nextTurn >= maxConversationTurns) {
      // Final turn - transition to plan formulation
      const planPrompt = `
      Based on this conversation about improving "${selectedFactor.factor_name}", generate 4-6 specific, actionable items for the manager's action plan.
      
      Factor context:
      - Current score: ${selectedFactor.latest_score.toFixed(2)}/10
      - Summary: ${selectedFactor.summary}
      
      Conversation:
      ${conversationContext}
      
      Generate practical actions that:
      - Are specific and measurable
      - Can be started within the next 2 weeks
      - Address the root causes discussed
      - Are appropriate for a manager to implement
      
      Return a JSON array of action items:
      ["Action 1", "Action 2", "Action 3", "Action 4", "Action 5", "Action 6"]
      `;
      
      try {
        const response = await callLLM(planPrompt, handleTokenUpdate);
        const actions = JSON.parse(response.content);
        setProposedActions(actions);
        setCurrentStep('formulate');
      } catch (error) {
        console.error('Error generating plan:', error);
        setProposedActions([
          "Review team feedback and identify specific pain points",
          "Schedule one-on-one discussions with team members",
          "Implement weekly check-ins to monitor progress",
          "Create clear communication channels for concerns"
        ]);
        setCurrentStep('formulate');
      }
    } else {
      // Continue conversation
      const continuePrompt = `
      You are facilitating an action planning session. This is question ${nextTurn} of ${maxConversationTurns}.
      
      Factor being discussed: "${selectedFactor.factor_name}" (scored ${selectedFactor.latest_score.toFixed(2)}/10)
      
      Conversation so far:
      ${conversationContext}
      
      Ask the next thoughtful question to help the manager build their action plan. Focus on:
      ${nextTurn === 2 ? 'Understanding specific situations or examples where this factor is problematic' :
        nextTurn === 3 ? 'Exploring what success would look like and identifying potential solutions' :
        nextTurn === 4 ? 'Discussing resources, stakeholders, and implementation considerations' :
        'Finalizing priorities and next steps before we create the action plan'}
      
      Keep it conversational and build on their previous responses. Return only the question.
      `;
      
      try {
        const response = await callLLM(continuePrompt, handleTokenUpdate);
        setConversationHistory(prev => [...prev, {
          type: 'assistant',
          message: response.content,
          timestamp: new Date().toISOString()
        }]);
        setConversationTurn(nextTurn);
      } catch (error) {
        console.error('Error continuing conversation:', error);
      }
    }
    
    setIsLoading(false);
  };

  // Update proposed action
  const updateAction = (index, newValue) => {
    const updated = [...proposedActions];
    updated[index] = newValue;
    setProposedActions(updated);
  };

  // Add new action
  const addAction = () => {
    setProposedActions([...proposedActions, ""]);
  };

  // Remove action
  const removeAction = (index) => {
    setProposedActions(proposedActions.filter((_, i) => i !== index));
  };

  // Complete plan
  const completePlan = () => {
    const finalActions = proposedActions.filter(action => action.trim());
    savePlan(finalActions);
    
    // Reset state
    setCurrentStep('overview');
    setSelectedReport(null);
    setSelectedFactor(null);
    setConversationHistory([]);
    setProposedActions([]);
    setConversationTurn(0);
    setCurrentInput('');
  };

  // Render functions for each step
  const renderOverview = () => (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Action Plan Center</Title>
          <Subtitle>Create and manage action plans based on culture audit insights</Subtitle>
        </HeaderContent>
        <PrimaryButton onClick={() => setCurrentStep('selectReport')}>
          Create New Plan
        </PrimaryButton>
      </Header>

      {/* Active Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle>Active Plans</CardTitle>
        </CardHeader>
        
        {activePlans.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </EmptyIcon>
            <EmptyTitle>No action plans yet</EmptyTitle>
            <EmptyDescription>Create your first action plan to get started</EmptyDescription>
            <PrimaryButton onClick={() => setCurrentStep('selectReport')}>
              Create Plan
            </PrimaryButton>
          </EmptyState>
        ) : (
          <CardContent style={{ padding: 0 }}>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Organization</TableHeaderCell>
                  <TableHeaderCell>Factor</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {activePlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>{plan.reportName}</TableCell>
                    <TableCell className="secondary">{plan.factorName}</TableCell>
                    <TableCell className="secondary">{plan.actions.length} actions</TableCell>
                    <TableCell className="secondary">
                      {new Date(plan.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge>{plan.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        )}
      </Card>
    </Container>
  );

  const renderSelectReport = () => (
    <ContainerMd>
      <div>
        <BackButton onClick={() => setCurrentStep('overview')}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Overview
        </BackButton>
        <Title>Select Report Source</Title>
        <Subtitle>Choose which organization's report to create an action plan for</Subtitle>
      </div>

      <Grid style={{ marginTop: '32px' }}>
        {reportData.map((report) => (
          <ReportCard
            key={report.anchor_id}
            onClick={() => {
              setSelectedReport(report);
              setCurrentStep('selectFactor');
            }}
          >
            <ReportHeader>
              <ReportTitle>{report.anchor_name}</ReportTitle>
              <ScoreDisplay>
                <ScoreValue>{report.overall_score.toFixed(1)}</ScoreValue>
                <ScoreLabel>Overall Score</ScoreLabel>
              </ScoreDisplay>
            </ReportHeader>
            
            <MetricsGrid>
              <MetricItem>
                <MetricLabel>Participation Rate</MetricLabel>
                <MetricValue>
                  {report.participation_rate.completed}/{report.participation_rate.total} 
                  ({Math.round((report.participation_rate.completed / report.participation_rate.total) * 100)}%)
                </MetricValue>
              </MetricItem>
              <MetricItem>
                <MetricLabel>Score Change</MetricLabel>
                <MetricValue className={report.score_change >= 0 ? 'positive' : 'negative'}>
                  {report.score_change >= 0 ? '+' : ''}{report.score_change.toFixed(1)}%
                </MetricValue>
              </MetricItem>
            </MetricsGrid>

            <FactorTags>
              <TagsLabel>Lowest Performing Factors</TagsLabel>
              <TagsContainer>
                {report.lowest_3_factors_analysis.map((factor) => (
                  <FactorTag key={factor.factor_name}>
                    {factor.factor_name} ({factor.latest_score.toFixed(1)})
                  </FactorTag>
                ))}
              </TagsContainer>
            </FactorTags>
          </ReportCard>
        ))}
      </Grid>
    </ContainerMd>
  );

  const renderSelectFactor = () => (
    <ContainerMd>
      <div>
        <BackButton onClick={() => setCurrentStep('selectReport')}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Report Selection
        </BackButton>
        <Title>Select Factor to Address</Title>
        <Subtitle>Choose from the lowest performing factors for {selectedReport?.anchor_name}</Subtitle>
      </div>

      <Grid style={{ marginTop: '32px' }}>
        {selectedReport?.lowest_3_factors_analysis.map((factor) => (
          <FactorCard
            key={factor.factor_name}
            onClick={() => {
              setSelectedFactor(factor);
              startConversation(factor);
            }}
          >
            <FactorHeader>
              <FactorTitle>{factor.factor_name}</FactorTitle>
              <FactorScore>
                <FactorScoreValue>{factor.latest_score.toFixed(1)}</FactorScoreValue>
                <ScoreLabel>Current Score</ScoreLabel>
              </FactorScore>
            </FactorHeader>

            <FactorSection>
              <SectionTitle>Definition</SectionTitle>
              <SectionContent>{factor.definition}</SectionContent>
            </FactorSection>

            <FactorSection>
              <SectionTitle>Current Situation</SectionTitle>
              <SectionContent>{factor.summary}</SectionContent>
            </FactorSection>

            {factor.analysis?.what_this_means_for_you && (
              <FactorSection>
                <SectionTitle>Key Challenges</SectionTitle>
                <ChallengesList>
                  {factor.analysis.what_this_means_for_you.map((point, idx) => (
                    <ChallengeItem key={idx}>{point}</ChallengeItem>
                  ))}
                </ChallengesList>
              </FactorSection>
            )}
          </FactorCard>
        ))}
      </Grid>
    </ContainerMd>
  );

  const renderConversation = () => (
    <Container>
      <ConversationContainer>
        <ConversationHeader>
          <ConversationInfo>
            <ConversationTitle>Planning Conversation</ConversationTitle>
            <ConversationSubtitle>
              Building an action plan for {selectedFactor?.factor_name} • Turn {conversationTurn} of {maxConversationTurns}
            </ConversationSubtitle>
          </ConversationInfo>
          <TokenInfo>
            Tokens used: {tokenUsage.total_tokens} • Est. cost: ${tokenUsage.total_cost.toFixed(4)}
          </TokenInfo>
        </ConversationHeader>
        
        {/* Progress bar */}
        <ProgressBar>
          <ProgressFill style={{ width: `${(conversationTurn / maxConversationTurns) * 100}%` }} />
        </ProgressBar>
      </ConversationContainer>

      {/* Chat interface */}
      <ChatContainer>
        <ChatMessages>
          {conversationHistory.map((msg, idx) => (
            <MessageContainer key={idx} isUser={msg.type === 'user'}>
              <Message isUser={msg.type === 'user'}>
                {msg.message}
              </Message>
            </MessageContainer>
          ))}
          
          {isLoading && (
            <LoadingIndicator>
              <LoadingMessage>
                <LoadingDot />
                <LoadingDot />
                <LoadingDot />
              </LoadingMessage>
            </LoadingIndicator>
          )}
          
          <div ref={chatEndRef} />
        </ChatMessages>
        
        {/* Input area */}
        <ChatInput>
          <InputContainer>
            <TextInput
              ref={textareaRef}
              value={currentInput}
              onChange={(e) => {
                setCurrentInput(e.target.value);
                autoResizeTextarea();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
                  e.preventDefault();
                  handleConversationInput();
                }
              }}
              placeholder="Type your response... (Enter to send, Shift+Enter for new line)"
              disabled={isLoading}
              rows={1}
            />
            <SendButton
              onClick={handleConversationInput}
              disabled={isLoading || !currentInput.trim()}
            >
              Send
            </SendButton>
          </InputContainer>
        </ChatInput>
      </ChatContainer>
    </Container>
  );

  const renderFormulate = () => (
    <ContainerMd>
      <FormulateContainer>
        <FormulateTitle>Finalize Your Action Plan</FormulateTitle>
        <FormulateSubtitle>
          Review and edit the proposed actions for improving {selectedFactor?.factor_name}
        </FormulateSubtitle>
      </FormulateContainer>

      <FormulateCard>
        <ActionsHeader>
          <ActionsTitle>Proposed Actions</ActionsTitle>
          <ActionsDescription>Edit these actions to match your specific needs and context.</ActionsDescription>
        </ActionsHeader>

        <ActionsList>
          {proposedActions.map((action, idx) => (
            <ActionItem key={idx}>
              <ActionNumber>{idx + 1}</ActionNumber>
              <ActionTextarea
                value={action}
                onChange={(e) => updateAction(idx, e.target.value)}
                rows={2}
                placeholder="Enter action item..."
              />
              <ActionDeleteButton onClick={() => removeAction(idx)}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </ActionDeleteButton>
            </ActionItem>
          ))}
        </ActionsList>

        <FormulateFooter>
          <AddActionButton onClick={addAction}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Action
          </AddActionButton>
          
          <FooterButtons>
            <SecondaryButton onClick={() => setCurrentStep('conversation')}>
              Back to Conversation
            </SecondaryButton>
            <CompleteButton
              onClick={completePlan}
              disabled={proposedActions.filter(a => a.trim()).length === 0}
            >
              Complete Plan
            </CompleteButton>
          </FooterButtons>
        </FormulateFooter>
      </FormulateCard>
    </ContainerMd>
  );

  // Main render
  return (
    <AppContainer>
      {currentStep === 'overview' && renderOverview()}
      {currentStep === 'selectReport' && renderSelectReport()}
      {currentStep === 'selectFactor' && renderSelectFactor()}
      {currentStep === 'conversation' && renderConversation()}
      {currentStep === 'formulate' && renderFormulate()}
    </AppContainer>
  );
};

export default ActionPlanCenter;
